import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

const POLL_MS = 3000;
const DASHBOARD = /^(admin-|mgr-|enh-|pos-|payroll|crm-|cs-)/;
// Operational lists that must stay live across POS / admin / manager / enhancer dashboards.
const LIVE_OPS = /^(pos-(u-|appointments|balance|dashboard)|admin-(outstanding|manual-payments|gift-cards|appointments)|mgr-dash|enh-bookings)/;
const TOPIC_KEYS = {
  offerings: /service|offering|discover|storefront|location|enhancer|online|spotlight|package|showcase|gauge|course/,
  schedule: /event|schedule|cal|slot|avail|session|retreat|discover|storefront|online|booking|register/,
  bookings: /event|slot|avail|booking|register|session|ticket|schedule|cal|credit|order/,
  centers: /location|center|storefront|discover|service|offering/,
  enhancers: /enhancer|storefront|discover|showcase|member|profile|service|offering/,
  programs: /course|package|membership|program|learning|discover|join|plan/,
  gauge: /gauge/,
  content: /article|page|media|homepage|nav|testimonial|content|seo|help|hero|review|announce|banner|setting|menu|discover/,
  media: /media|stream|watch|replay|broadcast|podcast|event|discover|yt-/,
  shop: /product|vendor|marketplace|shop|gift|order|inv-|storefront|discover|spotlight/,
  community: /feed|post|group|steward|showcase|comment|suggest|discover/,
  misc: /./,
};

// Polls the backend change signal and refetches only the affected public listings.
export default function LiveSync() {
  const qc = useQueryClient();
  const last = useRef(null);
  useEffect(() => {
    let stop = false;
    let timer;
    const tick = async () => {
      if (!document.hidden) {
        try {
          const { data } = await api.get("/sync/version");
          const v = data?.v || {};
          if (last.current) {
            const changed = Object.keys(v).filter((t) => v[t] !== last.current[t]);
            const rx = changed.map((t) => TOPIC_KEYS[t]).filter(Boolean);
            if (rx.length) {
              qc.invalidateQueries({
                predicate: (q) => {
                  const k = String(q.queryKey?.[0] ?? "");
                  if (LIVE_OPS.test(k)) return changed.some((t) => t === "bookings" || t === "schedule");
                  return !DASHBOARD.test(k) && rx.some((r) => r.test(k));
                },
              });
            }
          }
          last.current = v;
        } catch { /* offline — try again next tick */ }
      }
      if (!stop) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [qc]);
  return null;
}
